// ============================================================================
// ROLE SERVICE
// ============================================================================
// Service layer for the Role Master + login-time merged-permissions fetch.

import { get, post, put, del } from '../client/axiosClient';
import { roleEndpoints, ROLE_BASE_PATH } from '../endpoints/role.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  Role,
  RoleListItem,
  CreateRoleRequest,
  UpdateRoleRequest,
  RoleSearchRequest,
  MergedPermissions,
} from '../types/role.types';

class RoleService {
  async getAll(params?: RoleSearchRequest): Promise<PaginatedResponse<RoleListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(ROLE_BASE_PATH, params)
      : roleEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<Role>> {
    const response = await get<ApiResponse<Role>>(roleEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<Role>> {
    const response = await get<ApiResponse<Role>>(roleEndpoints.getByCode(code));
    return response.data;
  }

  /**
   * Resolves merged permissions for the given role codes. Endpoint is
   * [AllowAnonymous] so it can be called at login before tokens are validated
   * against role attributes — but the server will only return rows it finds
   * for the given codes.
   */
  async getMergedByRoles(roleCodes: string[]): Promise<ApiResponse<MergedPermissions>> {
    if (!roleCodes || roleCodes.length === 0) {
      return {
        success: true,
        data: { roleCodes: [], visibleModules: [], moduleActions: {} },
      };
    }
    const response = await get<ApiResponse<MergedPermissions>>(
      roleEndpoints.byRoles(roleCodes),
    );
    return response.data;
  }

  async create(data: CreateRoleRequest): Promise<ApiResponse<Role>> {
    const response = await post<ApiResponse<Role>>(roleEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateRoleRequest): Promise<ApiResponse<Role>> {
    const response = await put<ApiResponse<Role>>(roleEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(roleEndpoints.delete(id));
    return response.data;
  }

  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    const endpoint = excludeId
      ? `${roleEndpoints.validateCode(code)}?excludeId=${excludeId}`
      : roleEndpoints.validateCode(code);
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

export const roleService = new RoleService();
export default roleService;
